import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import type { VoiceDictationState } from '../hooks/useVoiceDictation';

interface VoiceDictationButtonProps {
  state: VoiceDictationState;
  onStart: () => void;
  onStop: () => void;
  duration?: number;
}

/**
 * Microphone button for voice dictation.
 * Shows different states: idle, recording (pulsing), processing (spinner).
 */
export function VoiceDictationButton({
  state,
  onStart,
  onStop,
  duration = 0,
}: VoiceDictationButtonProps) {
  const { t } = useTranslation('ai');

  const handleClick = () => {
    if (state === 'recording') {
      onStop();
    } else if (state === 'idle' || state === 'error') {
      onStart();
    }
  };

  const tooltipText =
    state === 'recording'
      ? t('quickCreate.voiceDictation.stop')
      : t('quickCreate.voiceDictation.start');

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative size-9 shrink-0"
          onClick={handleClick}
          disabled={state === 'processing'}
          aria-label={tooltipText}
          data-testid="ai-voice-dictation-button"
        >
          {state === 'processing' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : state === 'recording' ? (
            <>
              <MicOff className="size-4 text-destructive" />
              <span className="absolute -top-1 -right-1 flex size-3">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex size-3 rounded-full bg-destructive" />
              </span>
              {duration > 0 && (
                <span
                  className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-muted-foreground"
                  data-testid="ai-voice-dictation-timer"
                >
                  {formatDuration(duration)}
                </span>
              )}
            </>
          ) : (
            <Mic className="size-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {state === 'recording'
          ? t('quickCreate.voiceDictation.stop')
          : t('quickCreate.voiceDictation.start')}
      </TooltipContent>
    </Tooltip>
  );
}
